import React, { ChangeEvent, FormEvent, ReactNode, useRef, useState } from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { useUniqueWcagValues } from 'common-react';
import {
  Button,
  Card,
  FieldErrorMessage,
  FormFieldset,
  FormLegend,
  FormControlLabel,
  NumberInput,
  Radio,
  RadioGroup,
  RadioGroupVariant,
  TextField,
  Tooltip,
  Typography,
} from 'ui-components';
import {
  BIRTH_YEAR_LENGTH,
  CALC_FORM_TEST_ID,
  EmploymentType,
  Gender,
  MIN_MONTHLY_GROSS_SALARY,
  RETIREMENT_CALCULATOR_NAMESPACE,
  RETIREMENT_CALCULATOR_STEPS_COUNT,
  RetirementCalculatorField,
  RetirementCalculatorStep,
} from '../../tools/RetirementCalculator.const';
import {
  RetirementCalculatorErrorCode,
  RetirementCalculatorFormPropsModel,
} from '../../tools/RetirementCalculator.model';
import { sanitizeBirthYearInput } from '../../tools/RetirementCalculator.utils';
import { useRetirementCalculatorForm } from '../../tools/useRetirementCalculatorForm';

import useStyles from './RetirementCalculatorForm.style';

export const RetirementCalculatorForm: React.FC<RetirementCalculatorFormPropsModel> = ({
  titleId,
  title,
  submitLabel,
  salaryTooltip,
  initialValues,
  onSubmit,
  disabled = false,
  isLoading = false,
  className,
}) => {
  const c = useStyles({});
  const [t] = useTranslation(RETIREMENT_CALCULATOR_NAMESPACE, { useSuspense: false });
  const { getUniqueValue } = useUniqueWcagValues();
  const { values, errors, setFieldValue, submit } = useRetirementCalculatorForm({
    initialValues,
    onSubmit,
  });

  const [focusedField, setFocusedField] = useState<RetirementCalculatorField>();

  const salaryRef = useRef<HTMLInputElement>(null);
  const birthYearRef = useRef<HTMLInputElement>(null);
  const employmentTypeRef = useRef<HTMLDivElement>(null);
  const genderRef = useRef<HTMLDivElement>(null);

  const fieldIds: Record<RetirementCalculatorField, string> = {
    [RetirementCalculatorField.MONTHLY_GROSS_SALARY]: getUniqueValue(
      RetirementCalculatorField.MONTHLY_GROSS_SALARY,
    ),
    [RetirementCalculatorField.EMPLOYMENT_TYPE]: getUniqueValue(
      RetirementCalculatorField.EMPLOYMENT_TYPE,
    ),
    [RetirementCalculatorField.BIRTH_YEAR]: getUniqueValue(RetirementCalculatorField.BIRTH_YEAR),
    [RetirementCalculatorField.GENDER]: getUniqueValue(RetirementCalculatorField.GENDER),
  };
  const getDescriptionId = (field: RetirementCalculatorField) => `${fieldIds[field]}-description`;

  const errorMessages: Record<RetirementCalculatorErrorCode, Record<string, string>> = {
    [RetirementCalculatorErrorCode.REQUIRED]: {
      [RetirementCalculatorField.MONTHLY_GROSS_SALARY]: t(
        'form.monthly-gross-salary.error.required',
      ),
      [RetirementCalculatorField.EMPLOYMENT_TYPE]: t('form.employment-type.error.required'),
      [RetirementCalculatorField.BIRTH_YEAR]: t('form.birth-year.error.required'),
      [RetirementCalculatorField.GENDER]: t('form.gender.error.required'),
    },
    [RetirementCalculatorErrorCode.SALARY_BELOW_MIN]: {
      [RetirementCalculatorField.MONTHLY_GROSS_SALARY]: t(
        'form.monthly-gross-salary.error.min',
        { min: MIN_MONTHLY_GROSS_SALARY },
      ),
    },
    [RetirementCalculatorErrorCode.AGE_OUT_OF_RANGE]: {
      [RetirementCalculatorField.BIRTH_YEAR]: t('form.birth-year.error.age-out-of-range'),
    },
  };

  const getErrorMessage = (field: RetirementCalculatorField) => {
    const errorCode = errors[field];
    return errorCode ? errorMessages[errorCode][field] : undefined;
  };

  const salaryError = getErrorMessage(RetirementCalculatorField.MONTHLY_GROSS_SALARY);
  const employmentTypeError = getErrorMessage(RetirementCalculatorField.EMPLOYMENT_TYPE);
  const birthYearError = getErrorMessage(RetirementCalculatorField.BIRTH_YEAR);
  const genderError = getErrorMessage(RetirementCalculatorField.GENDER);

  const focusTargets: Record<RetirementCalculatorField, () => HTMLElement | null | undefined> = {
    [RetirementCalculatorField.MONTHLY_GROSS_SALARY]: () => salaryRef.current,
    [RetirementCalculatorField.EMPLOYMENT_TYPE]: () =>
      employmentTypeRef.current?.querySelector('input'),
    [RetirementCalculatorField.BIRTH_YEAR]: () => birthYearRef.current,
    [RetirementCalculatorField.GENDER]: () => genderRef.current?.querySelector('input'),
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const firstInvalidField = submit();
    if (firstInvalidField) {
      focusTargets[firstInvalidField]()?.focus();
    }
  };

  const handleBirthYearChange = (event: ChangeEvent<HTMLInputElement>) => {
    const sanitizedValue = sanitizeBirthYearInput(event.target.value, BIRTH_YEAR_LENGTH);
    setFieldValue(
      RetirementCalculatorField.BIRTH_YEAR,
      sanitizedValue ? Number(sanitizedValue) : undefined,
    );
  };

  const focusHandlers = (field: RetirementCalculatorField) => ({
    onFocus: () => setFocusedField(field),
    onBlur: () => setFocusedField(current => (current === field ? undefined : current)),
  });

  const renderDescription = (field: RetirementCalculatorField, text: ReactNode) => (
    <div
      className={clsx(c.descriptionWrapper, focusedField !== field && c.descriptionWrapperHidden)}
    >
      <div className={c.descriptionClip}>
        <Typography
          variant="body2"
          component="p"
          id={getDescriptionId(field)}
          className={c.description}
        >
          {text}
        </Typography>
      </div>
    </div>
  );

  const salaryLabel = (
    <span className={c.labelWithTooltip}>
      {t('form.monthly-gross-salary.label')}
      {salaryTooltip && <Tooltip title={salaryTooltip} className={c.labelTooltip} />}
    </span>
  );

  return (
    <Card className={clsx(c.root, className)} size="big" testId={CALC_FORM_TEST_ID}>
      <form className={c.form} onSubmit={handleSubmit} noValidate={true} autoComplete="off">
        <div className={c.header}>
          <Typography
            variant="h1"
            component="h1"
            fontWeight="extraBold"
            textColor="maroons.100"
            id={titleId}
            className={c.title}
            testId={`${CALC_FORM_TEST_ID}-title`}
          >
            {title ?? t('title')}
          </Typography>
          <div className={c.stepHeader}>
            <Typography
              variant="subHeadline2"
              component="p"
              fontWeight="bold"
              textColor="oranges.50"
              className={c.stepHeading}
            >
              {t('step-label', {
                step: RetirementCalculatorStep.BASIC_DATA,
                total: RETIREMENT_CALCULATOR_STEPS_COUNT,
              })}
            </Typography>
            <Typography
              variant="subHeadline2"
              component="h2"
              fontWeight="bold"
              className={c.stepHeading}
            >
              {t('subtitle')}
            </Typography>
          </div>
        </div>

        <div className={c.fields}>
          <div className={c.field}>
            <NumberInput
              ref={salaryRef}
              id={fieldIds[RetirementCalculatorField.MONTHLY_GROSS_SALARY]}
              name={RetirementCalculatorField.MONTHLY_GROSS_SALARY}
              className={c.input}
              label={salaryLabel}
              adornment="PLN"
              placeholder={t('form.monthly-gross-salary.placeholder')}
              decimalScale={2}
              fixedDecimalScale={false}
              value={values[RetirementCalculatorField.MONTHLY_GROSS_SALARY]}
              onValueChange={value =>
                setFieldValue(RetirementCalculatorField.MONTHLY_GROSS_SALARY, value)
              }
              required={true}
              error={!!salaryError}
              errorMessage={salaryError}
              ariaRequiredId={`${fieldIds[RetirementCalculatorField.MONTHLY_GROSS_SALARY]}-error`}
              ariaDescribedBy={getDescriptionId(RetirementCalculatorField.MONTHLY_GROSS_SALARY)}
              testId={`${CALC_FORM_TEST_ID}-salary`}
              {...focusHandlers(RetirementCalculatorField.MONTHLY_GROSS_SALARY)}
            />
            {renderDescription(
              RetirementCalculatorField.MONTHLY_GROSS_SALARY,
              t('form.monthly-gross-salary.description'),
            )}
          </div>

          <FormFieldset
            className={c.fieldset}
            ariaLabelledBy={`${fieldIds[RetirementCalculatorField.EMPLOYMENT_TYPE]}-legend`}
          >
            <FormLegend
              id={`${fieldIds[RetirementCalculatorField.EMPLOYMENT_TYPE]}-legend`}
              className={c.legend}
            >
              <Typography variant="body1" component="span" fontWeight="bold">
                {t('form.employment-type.label')}
              </Typography>
            </FormLegend>
            <div ref={employmentTypeRef} {...focusHandlers(RetirementCalculatorField.EMPLOYMENT_TYPE)}>
              <RadioGroup
                name={RetirementCalculatorField.EMPLOYMENT_TYPE}
                className={c.radioGroup}
                variant={RadioGroupVariant.HORIZONTAL}
                value={values[RetirementCalculatorField.EMPLOYMENT_TYPE] ?? ''}
                onChange={(_event, value) =>
                  setFieldValue(RetirementCalculatorField.EMPLOYMENT_TYPE, value as EmploymentType)
                }
                required={true}
                hasError={!!employmentTypeError}
                ariaRequiredId={`${fieldIds[RetirementCalculatorField.EMPLOYMENT_TYPE]}-error`}
                testId={`${CALC_FORM_TEST_ID}-employmentType`}
              >
                <FormControlLabel
                  value={EmploymentType.EMPLOYMENT_CONTRACT}
                  control={
                    <Radio
                      hasError={!!employmentTypeError}
                      inputProps={{
                        'aria-describedby': getDescriptionId(
                          RetirementCalculatorField.EMPLOYMENT_TYPE,
                        ),
                      }}
                    />
                  }
                  label={t('form.employment-type.employment-contract')}
                />
                <FormControlLabel
                  value={EmploymentType.BUSINESS}
                  control={
                    <Radio
                      hasError={!!employmentTypeError}
                      inputProps={{
                        'aria-describedby': getDescriptionId(
                          RetirementCalculatorField.EMPLOYMENT_TYPE,
                        ),
                      }}
                    />
                  }
                  label={t('form.employment-type.business')}
                />
              </RadioGroup>
            </div>
            {employmentTypeError && (
              <FieldErrorMessage
                id={`${fieldIds[RetirementCalculatorField.EMPLOYMENT_TYPE]}-error`}
                className={c.fieldsetError}
              >
                {employmentTypeError}
              </FieldErrorMessage>
            )}
            {renderDescription(
              RetirementCalculatorField.EMPLOYMENT_TYPE,
              t('form.employment-type.description'),
            )}
          </FormFieldset>

          <div className={c.field}>
            <TextField
              inputRef={birthYearRef}
              id={fieldIds[RetirementCalculatorField.BIRTH_YEAR]}
              name={RetirementCalculatorField.BIRTH_YEAR}
              className={clsx(c.input, c.suffixInput)}
              label={t('form.birth-year.label')}
              placeholder={t('form.birth-year.placeholder')}
              endAdornment={t('form.birth-year.suffix')}
              value={values[RetirementCalculatorField.BIRTH_YEAR] ?? ''}
              onChange={handleBirthYearChange}
              inputMode="numeric"
              autoComplete="bday-year"
              required={true}
              error={birthYearError}
              errorId={`${fieldIds[RetirementCalculatorField.BIRTH_YEAR]}-error`}
              inputProps={{
                maxLength: BIRTH_YEAR_LENGTH,
                'aria-describedby': getDescriptionId(RetirementCalculatorField.BIRTH_YEAR),
              }}
              testId={`${CALC_FORM_TEST_ID}-birthYear`}
              {...focusHandlers(RetirementCalculatorField.BIRTH_YEAR)}
            />
            {renderDescription(
              RetirementCalculatorField.BIRTH_YEAR,
              t('form.birth-year.description'),
            )}
          </div>

          <FormFieldset
            className={c.fieldset}
            ariaLabelledBy={`${fieldIds[RetirementCalculatorField.GENDER]}-legend`}
          >
            <FormLegend
              id={`${fieldIds[RetirementCalculatorField.GENDER]}-legend`}
              className={c.legend}
            >
              <Typography variant="body1" component="span" fontWeight="bold">
                {t('form.gender.label')}
              </Typography>
            </FormLegend>
            <div ref={genderRef} {...focusHandlers(RetirementCalculatorField.GENDER)}>
              <RadioGroup
                name={RetirementCalculatorField.GENDER}
                className={c.radioGroup}
                variant={RadioGroupVariant.HORIZONTAL}
                value={values[RetirementCalculatorField.GENDER] ?? ''}
                onChange={(_event, value) =>
                  setFieldValue(RetirementCalculatorField.GENDER, value as Gender)
                }
                required={true}
                hasError={!!genderError}
                ariaRequiredId={`${fieldIds[RetirementCalculatorField.GENDER]}-error`}
                testId={`${CALC_FORM_TEST_ID}-gender`}
              >
                <FormControlLabel
                  value={Gender.FEMALE}
                  control={
                    <Radio
                      hasError={!!genderError}
                      inputProps={{
                        'aria-describedby': getDescriptionId(RetirementCalculatorField.GENDER),
                      }}
                    />
                  }
                  label={t('form.gender.female')}
                />
                <FormControlLabel
                  value={Gender.MALE}
                  control={
                    <Radio
                      hasError={!!genderError}
                      inputProps={{
                        'aria-describedby': getDescriptionId(RetirementCalculatorField.GENDER),
                      }}
                    />
                  }
                  label={t('form.gender.male')}
                />
              </RadioGroup>
            </div>
            {genderError && (
              <FieldErrorMessage
                id={`${fieldIds[RetirementCalculatorField.GENDER]}-error`}
                className={c.fieldsetError}
              >
                {genderError}
              </FieldErrorMessage>
            )}
            {renderDescription(
              RetirementCalculatorField.GENDER,
              t('form.gender.description'),
            )}
          </FormFieldset>
        </div>

        <Button
          type="submit"
          className={c.submitButton}
          disabled={disabled}
          isLoading={isLoading}
          testId={`${CALC_FORM_TEST_ID}-submit`}
        >
          {submitLabel ?? t('form.submit')}
        </Button>
      </form>
    </Card>
  );
};
